// Session refresh policy shared by `authMiddleware`. Kept free of app imports
// so it runs under `node --test` (see `../tests/sessionRefresh.test.mjs`).

interface SessionRefreshOptions {
  // Calls the refresh endpoint. Must throw on any non-2xx response, which is
  // what `fetchClient` does through `errorMiddleware`.
  refresh: () => Promise<unknown>;
  // Runs when the refresh endpoint definitively rejects the session.
  onSessionLost: () => void;
  // Serializes the refresh with other browser tabs; defaults to no lock.
  withLock?: (task: () => Promise<boolean>) => Promise<boolean>;
}

// Returns a `refreshSession` that resolves `true` when the session is valid
// again and `false` when the original request should fail.
//
// One refresh at a time, per tab and across tabs. Several requests commonly
// 401 together (for example focus refetches after the session cookie expired),
// and with refresh-token rotation a concurrent refresh would present an
// already-rotated token, fail, and sign the user out right after another one
// succeeded. Tabs share the cookie jar but not module state, so the cross-tab
// lock makes a waiting tab refresh with the rotated cookies instead.
export const createSessionRefresh = ({
  refresh,
  onSessionLost,
  withLock = (task) => task(),
}: SessionRefreshOptions) => {
  let inFlightRefresh: Promise<boolean> | null = null;

  const requestRefresh = async () => {
    try {
      await refresh();
      return true;
    } catch (error) {
      // Only a definitive rejection means the session is gone. A network
      // failure, 5xx or 429 keeps the cached user; the original request fails
      // and the next request tries to refresh again.
      if (isUnauthorized(error)) {
        onSessionLost();
      }
      return false;
    }
  };

  return () => {
    if (inFlightRefresh) {
      return inFlightRefresh;
    }

    inFlightRefresh = withLock(requestRefresh).finally(() => {
      inFlightRefresh = null;
    });

    return inFlightRefresh;
  };
};

export const isUnauthorized = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  error.status === 401;

const REFRESH_LOCK = 'finlay-auth-refresh';

export const withCrossTabLock = (task: () => Promise<boolean>) =>
  typeof navigator === 'object' && 'locks' in navigator
    ? navigator.locks.request(REFRESH_LOCK, task)
    : task();
